// TomTom Traffic API — segunda fonte de incidentes (acidentes, obras,
// vias cortadas) que complementa o HERE. A cobertura em PT é diferente:
// onde o HERE não tem dados em zonas inter-urbanas, o TomTom muitas
// vezes tem, e vice-versa. As duas fontes são agregadas e deduplicadas
// por proximidade ~50m em fetchIncidentsAggregated().
//
// Documentação:
//   https://developer.tomtom.com/traffic-api/documentation/traffic-incidents-service/incident-details
//
// Tier gratuito: 2.500 pedidos/dia. A chave é configurada no .env como
// REACT_APP_TOMTOM_API_KEY. Se não estiver configurada, todas as
// funções devolvem [] silenciosamente — o sistema continua a funcionar
// com HERE apenas.

const TOMTOM_KEY = process.env.REACT_APP_TOMTOM_API_KEY;
const BASE_URL = 'https://api.tomtom.com/traffic/services/5/incidentDetails';

// TomTom incident category → tipo canónico do nosso planner.
// Mantém-se alinhado com o INCIDENT_TYPE_MAP em hereApi.js (mesmos
// códigos 0-4) para que os marcadores no mapa sejam consistentes.
//
//   0 = Acidente   (red 🚨)
//   1 = Congestionamento (orange 🟠)
//   2 = Obras   (yellow 🚧)
//   3 = Estrada Cortada (red ⛔)
//   4 = Outros (gray ⚠️)
function tomtomCategoryToType(cat) {
  // TomTom iconCategory codes (subset relevant para PT):
  //   0  Unknown
  //   1  Accident
  //   2  Fog
  //   3  DangerousConditions
  //   4  Rain
  //   5  Ice
  //   6  Jam
  //   7  LaneClosed
  //   8  RoadClosed
  //   9  RoadWorks
  //   10 Wind
  //   11 Flooding
  //   14 BrokenDownVehicle
  if (cat === 1) return 0;                // Accident
  if (cat === 6) return 1;                // Jam
  if (cat === 9 || cat === 7) return 2;   // Roadworks / lane closed
  if (cat === 8) return 3;                // Road closed
  return 4;                               // outros
}

// TomTom magnitudeOfDelay (0-4) → severity em texto, alinhada com o que
// o popup do mapa mostra hoje para HERE (minor / major / critical).
function tomtomMagnitudeToCriticality(mag) {
  if (mag >= 4) return 'critical';
  if (mag === 3) return 'major';
  if (mag === 2 || mag === 1) return 'minor';
  return 'lowImpact';
}

/**
 * Fetch traffic incidents from TomTom for a given bbox.
 *
 * @param {{north,south,east,west}} bounds
 * @returns {Promise<Array>}  same shape as fetchIncidentsForViewport (HERE)
 */
export async function fetchIncidentsTomTomBbox(bounds) {
  if (!TOMTOM_KEY || TOMTOM_KEY.includes('PLACEHOLDER') || !bounds) return [];

  const j = typeof bounds.toJSON === 'function' ? bounds.toJSON() : bounds;
  if (j.north == null || j.south == null || j.east == null || j.west == null) return [];

  // TomTom espera bbox como minLng,minLat,maxLng,maxLat
  const bbox = `${j.west},${j.south},${j.east},${j.north}`;

  // Field mask em formato GraphQL-ish. Pedimos o mínimo para deduzir
  // posição, tipo, severidade, descrição e horários.
  const fields = '{incidents{type,geometry{type,coordinates},properties{iconCategory,magnitudeOfDelay,events{description,iconCategory},startTime,endTime,from,to,delay,roadNumbers,probabilityOfOccurrence}}}';

  const url = `${BASE_URL}?key=${encodeURIComponent(TOMTOM_KEY)}`
    + `&bbox=${encodeURIComponent(bbox)}`
    + `&fields=${encodeURIComponent(fields)}`
    + `&language=pt-PT`
    + `&t=1000`;

  try {
    const res = await fetch(url);
    if (!res.ok) {
      // 403 typical = chave inválida ou serviço não activado. Falha silenciosa
      // para não interromper o HERE quando o TomTom está mal configurado.
      console.warn(`[TomTom] HTTP ${res.status} — incidents skipped`);
      return [];
    }
    const data = await res.json();
    const items = data?.incidents || [];

    return items.map(item => {
      const props = item.properties || {};
      const geom = item.geometry || {};
      // TomTom devolve LineString para troços, Point para pontos. Ambos
      // têm coordinates em [lng,lat] (GeoJSON). Para o marcador usamos
      // o primeiro ponto da geometria.
      let coords = null;
      if (geom.type === 'Point') coords = geom.coordinates;
      else if (geom.type === 'LineString' && Array.isArray(geom.coordinates)) {
        coords = geom.coordinates[0];
      } else if (geom.type === 'MultiLineString' && Array.isArray(geom.coordinates)) {
        coords = geom.coordinates[0]?.[0];
      }
      if (!Array.isArray(coords) || coords.length < 2) return null;

      const cat = props.iconCategory != null
        ? props.iconCategory
        : (props.events?.[0]?.iconCategory);
      const typeCode = tomtomCategoryToType(cat);

      // Construir descrição rica a partir dos events (TomTom mete
      // múltiplos eventos por incidente — ex: "Roadworks · Lane closed").
      const events = Array.isArray(props.events) ? props.events : [];
      const descriptions = events.map(e => e.description).filter(Boolean);
      const summary = descriptions.length > 0
        ? descriptions.join(' · ')
        : (typeCode === 8 ? 'Estrada cortada' : 'Incidente');

      // ID único para deduplicar com HERE (proximidade) — TomTom não
      // expõe um ID estável directamente, usamos coords como hash.
      const id = `tt-${coords[1].toFixed(5)}_${coords[0].toFixed(5)}`;

      return {
        id,
        type: typeCode,
        typeName: { // mesma shape que HERE para o popup do mapa
          label: ({ 0: 'Acidente', 1: 'Congestionamento', 2: 'Obras', 3: 'Estrada Cortada', 4: 'Outros' })[typeCode],
          emoji: ({ 0: '🚨', 1: '🟠', 2: '🚧', 3: '⛔', 4: '⚠️' })[typeCode],
          color: ({ 0: '#ef4444', 1: '#f97316', 2: '#eab308', 3: '#dc2626', 4: '#6b7280' })[typeCode],
        },
        description: summary,
        summary,
        criticality: tomtomMagnitudeToCriticality(props.magnitudeOfDelay),
        roadClosed: typeCode === 3 || cat === 8,
        coordinates: [coords[0], coords[1]],
        startTime: props.startTime || null,
        endTime: props.endTime || null,
        lastUpdated: null,
        // marca a fonte para que o popup mostre badge
        source: 'tomtom',
      };
    }).filter(Boolean);
  } catch (e) {
    console.warn('[TomTom] fetch failed:', e.message);
    return [];
  }
}
